/**
 * appUi — the presentation contract for the app's dense working surfaces.
 *
 * Started life as `chartsUi` for the Charts page. The Dashboard is the second
 * page to adopt it, and a shared module named after one page is exactly how a
 * codebase ends up with two competing type scales — which is the class of
 * problem this module exists to end.
 *
 * WHY THIS EXISTS
 * `colorTokens.ts` opens with the rule "Components must NEVER use raw hex values
 * inline". The Charts page had drifted to 240 raw hex/rgba literals against 40
 * token references, 15 distinct font sizes between 8px and 13.6px, and 13
 * toolbar group pills each in its own pastel tint. The result read as an
 * internal tool: nothing on it told you what mattered, because everything was
 * shouting equally.
 *
 * THE RULES THIS FILE ENCODES
 *   1. Four type sizes. Nothing else, and nothing below 11px.
 *   2. Containers are NEUTRAL — surface plus a hairline border. A container
 *      never carries a hue of its own.
 *   3. Colour means STATE, not category. Blue = this control is on. Green/red
 *      are reserved for genuinely bullish/bearish things and for trade actions.
 *      Groups are told apart by their labels, which is what labels are for.
 *   4. Every colour resolves through `tokens[mode]`.
 *
 * The Dashboard's failure mode was different from the Charts page's — its colour
 * tokens were already well adopted (334 references against 28 raw literals), but
 * it had 21 declared font sizes and its MOST COMMON rendered text was 9.92px.
 * Rule 1 is what that page needed.
 *
 * Import the helpers rather than re-deriving these values, so the page stays
 * consistent as it changes.
 */
import type { AppColorTokens } from './colorTokens';

/** Type scale — 11 / 12 / 13 / 15px. The only sizes the Charts page may use. */
export const CH_TYPE = {
  /** 11px — group labels, dense metadata, axis-adjacent captions. The floor. */
  micro: '0.6875rem',
  /** 12px — chips and most controls. The default. */
  small: '0.75rem',
  /** 13px — panel values, the OHLC legend, anything meant to be read. */
  body: '0.8125rem',
  /** 15px — the symbol, pane titles. */
  title: '0.9375rem',
} as const;

/** Density scale — one rhythm for control heights and spacing. */
export const CH_SIZE = {
  /* 21, not 22. Measured: at 1366px the outlined chips are wider than the old
     filled ones (they carry a border), and a 22px chip pushed the toolbar to an
     extra wrapped row — undoing the row the Indicators collapse had just won
     back. One pixel of height and a little horizontal padding buys that row back
     at 1366 while staying above the 11px type floor. */
  chipH: 21,
  controlH: 26,
  groupPadX: 0.6,
  groupPadY: 0.25,
  groupGap: 0.3,
  rowGap: 0.4,
  radius: 1.2,
} as const;

/** Hairline border — the only border weight on the page. */
export const chHairline = (isDark: boolean): string =>
  isDark ? 'rgba(255,255,255,0.11)' : 'rgba(15,23,42,0.13)';

/** Neutral surface, slightly lifted off the page so a group reads as a unit
 *  without taking on a colour of its own. */
export const chSurface = (isDark: boolean): string =>
  isDark ? 'rgba(255,255,255,0.045)' : 'rgba(15,23,42,0.035)';

/** A panel floating OVER the chart. Translucent + blurred so candles read
 *  through it — an opaque panel is a hole punched in the price action. */
export const chOverlaySurface = (isDark: boolean) => ({
  bgcolor: isDark ? 'rgba(13,17,23,0.82)' : 'rgba(255,255,255,0.86)',
  border: `1px solid ${chHairline(isDark)}`,
  borderRadius: CH_SIZE.radius,
  backdropFilter: 'blur(6px)',
  boxShadow: isDark
    ? '0 2px 10px rgba(0,0,0,0.45)'
    : '0 2px 10px rgba(15,23,42,0.10)',
});

/** The ONE toolbar-group container. Every group uses this — the rainbow of
 *  per-group tints is deliberately gone. */
export const chGroupSx = (isDark: boolean, dense = false) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: dense ? 0.25 : CH_SIZE.groupGap,
  flexWrap: 'wrap' as const,
  px: dense ? 0.45 : CH_SIZE.groupPadX,
  py: dense ? 0.2 : CH_SIZE.groupPadY,
  borderRadius: CH_SIZE.radius,
  bgcolor: chSurface(isDark),
  border: `1px solid ${chHairline(isDark)}`,
});

/** Uppercase group label. Small, quiet, and the thing that actually
 *  distinguishes one group from another now that the tints are gone. */
export const chGroupLabelSx = {
  fontSize: CH_TYPE.micro,
  fontWeight: 700,
  letterSpacing: 0.4,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  mr: 0.3,
  userSelect: 'none' as const,
  whiteSpace: 'nowrap' as const,
};

/** Semantic tone for a control. `accent` is "this is switched on" and is the
 *  default; the rest are reserved for things that really are directional. */
export type ChTone = 'accent' | 'bull' | 'bear' | 'warn';

const toneColor = (C: AppColorTokens, tone: ChTone) =>
  tone === 'bull' ? C.bull : tone === 'bear' ? C.bear : tone === 'warn' ? C.warn : C.neutral;

/**
 * The ONE toolbar chip. Inactive is a quiet outline; active fills with the
 * tone. Because the default tone is `accent`, a glance at the toolbar now
 * answers "what is switched on?" instead of "what colour is this group?".
 */
export const chChipSx = (
  C: AppColorTokens,
  isDark: boolean,
  opts: { active?: boolean; tone?: ChTone; dense?: boolean } = {},
) => {
  const { active = false, tone = 'accent', dense = false } = opts;
  const col = toneColor(C, tone);
  return {
    cursor: 'pointer',
    /* `dense` is for the SIDE PANEL, which is ~190px wide and user-resizable.
       The toolbar chip size makes its filter groups wrap to an extra line each,
       so there the chip tightens to the 11px floor — still legible, and it is
       the floor precisely so that "make it fit" can never go below it. */
    height: dense ? 19 : CH_SIZE.chipH,
    fontWeight: 600,
    fontSize: dense ? CH_TYPE.micro : CH_TYPE.small,
    borderRadius: 0.8,
    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
    '& .MuiChip-label': { px: dense ? 0.45 : 0.58 },
    /* Label colour follows the FILL, not the theme's text colour. colorTokens'
       stated principle is "dark mode -> bright variants", so on dark the fill is
       a bright #60A5FA / #2DD4AA and white text on it lands around 2.3:1 —
       unreadable. Dark text on those bright fills, white text on light mode's
       deep ones. */
    ...(active
      ? { bgcolor: col, color: isDark ? '#0B1220' : '#fff', borderColor: col,
          '&:hover': { bgcolor: col, filter: 'brightness(1.08)' } }
      : { bgcolor: 'transparent', color: 'text.secondary',
          border: `1px solid ${chHairline(isDark)}`,
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.055)',
                       color: 'text.primary' } }),
  };
};

/** Numbers line up only if they are tabular. Use on every price, count and
 *  percentage — this is why the axis and the legend look ragged today. */
export const chNumeric = {
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: '"tnum"',
} as const;

/** Pane title. Every chart pane header uses this, so a quad grid reads as four
 *  panes rather than four differently-weighted floating captions. The bottom
 *  hairline is what actually separates one pane's chrome from its chart. */
export const chPaneTitleSx = {
  fontWeight: 700,
  fontSize: CH_TYPE.title,
  lineHeight: 1.25,
  letterSpacing: 0.1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

/** The row a pane title sits in. */
export const chPaneHeaderSx = (isDark: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.6,
  px: 1,
  py: 0.4,
  minWidth: 0,
  borderBottom: `1px solid ${chHairline(isDark)}`,
});


/* ─────────────────────────── Dashboard surfaces ─────────────────────────── */

/**
 * Section header row. One header per section: the Dashboard used to print every
 * section's title TWICE — once in the collapse strip and again inside the card
 * below it — so this carries the title, its count, its help affordance and its
 * actions in a single row, and the card underneath carries none of them.
 */
export const chSectionHeaderSx = (isDark: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
  px: 1,
  py: 0.6,
  borderRadius: CH_SIZE.radius,
  cursor: 'pointer',
  userSelect: 'none' as const,
  transition: 'background-color 120ms ease',
  '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)' },
});

/** Section title. One weight, one size, across all eight sections. */
export const chSectionTitleSx = {
  fontSize: CH_TYPE.title,
  fontWeight: 700,
  letterSpacing: 0.1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

/**
 * KPI / metric tile. Every tile in a row gets the SAME shape — the Risk Cockpit
 * tile used to carry twice its siblings' flex weight while showing no value at
 * all, so the row read as five tiles plus one odd one out.
 */
export const chTileSx = (isDark: boolean) => ({
  flex: '1 1 150px',
  minWidth: 150,
  px: 1.25,
  py: 0.9,
  borderRadius: CH_SIZE.radius,
  border: `1px solid ${chHairline(isDark)}`,
  bgcolor: chSurface(isDark),
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 0.35,
  minHeight: 62,
});

/** Tile label — the small uppercase caption above the value. */
export const chTileLabelSx = {
  fontSize: CH_TYPE.micro,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  whiteSpace: 'nowrap' as const,
};

/** Tile value — the number itself. Tabular, so a row of tiles lines up. */
export const chTileValueSx = {
  fontSize: '1.05rem',
  fontWeight: 700,
  lineHeight: 1.2,
  ...chNumeric,
};
